"use client"

import { useState, useEffect } from "react"
import "./PlanetSelector.css"

const PlanetSelector = ({ onPlanetSelect, userLevel = 1, addNotification }) => {
  const [planets, setPlanets] = useState([])
  const [selectedPlanet, setSelectedPlanet] = useState(null)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [realTimeData, setRealTimeData] = useState({})

  useEffect(() => {
    initializePlanets()
    fetchRealTimeData()
    const interval = setInterval(fetchRealTimeData, 30000) // Update every 30 seconds
    return () => clearInterval(interval)
  }, [])

  const fetchRealTimeData = async () => {
    try {
      // Using your NASA API key for real data
      const response = await fetch(
        "https://api.nasa.gov/insight_weather/?api_key=YOUR_NASA_API_KEY&feedtype=json&ver=1.0",
      )
      const data = await response.json()

      // Process Mars weather data
      const sols = Object.keys(data).filter((key) => key !== "validity_checks")
      if (sols.length > 0) {
        const latestSol = data[sols[0]]
        setRealTimeData((prev) => ({
          ...prev,
          mars: {
            temperature: latestSol.AT?.av || -65,
            windSpeed: latestSol.HWS?.av || 5,
            pressure: latestSol.PRE?.av || 700,
            hasDustStorm: (latestSol.HWS?.av || 0) > 15,
          },
        }))
      }
    } catch (error) {
      console.log("Using fallback data for Mars weather")
      // Fallback with realistic variation
      setRealTimeData((prev) => ({
        ...prev,
        mars: {
          temperature: -65 + Math.random() * 20 - 10,
          windSpeed: 5 + Math.random() * 15,
          pressure: 700 + Math.random() * 200 - 100,
          hasDustStorm: Math.random() < 0.1,
        },
      }))
    }
  }

  const initializePlanets = () => {
    const planetData = [
      {
        id: "mars",
        name: "Mars",
        emoji: "🔴",
        difficulty: "MEDIUM",
        difficultyColor: "medium",
        description: "The Red Planet • Radiation storms and dust devils await",
        unlockLevel: 1,
        environment: {
          temperature: -65,
          gravity: 0.38,
          atmosphere: 0.6,
        },
        threats: ["Solar Radiation", "Dust Storms", "Perchlorate Soil"],
        resources: ["Iron Oxide", "Water Ice", "Carbon Dioxide"],
        sphere: {
          gradient: "radial-gradient(circle at 30% 30%, #ff6b6b, #d63031)",
          glow: "rgba(255, 107, 107, 0.5)",
        },
      },
      {
        id: "europa",
        name: "Europa",
        emoji: "🌙",
        difficulty: "HARD",
        difficultyColor: "hard",
        description: "Jupiter's ice moon harbors a vast subsurface ocean. Life may already exist here.",
        unlockLevel: 5,
        environment: {
          temperature: -180,
          gravity: 0.134,
          atmosphere: 0.13,
        },
        threats: ["Extreme Cold", "Radiation", "Thin Oxygen"],
        resources: ["Water Ice", "Oxygen", "Sulfur"],
        sphere: {
          gradient: "radial-gradient(circle at 30% 30%, #74b9ff, #0984e3)",
          glow: "rgba(116, 185, 255, 0.5)",
        },
      },
      {
        id: "titan",
        name: "Titan",
        emoji: "🟤",
        difficulty: "EXTREME",
        difficultyColor: "extreme",
        description: "Saturn's largest moon features methane lakes and a thick atmosphere.",
        unlockLevel: 10,
        environment: {
          temperature: -179,
          gravity: 0.14,
          atmosphere: 1.5,
        },
        threats: ["Methane Rain", "Extreme Cold", "Low Gravity"],
        resources: ["Methane", "Nitrogen", "Hydrocarbons"],
        sphere: {
          gradient: "radial-gradient(circle at 30% 30%, #fdcb6e, #e17055)",
          glow: "rgba(253, 203, 110, 0.5)",
        },
      },
    ]

    setPlanets(planetData)
  }

  const isPlanetUnlocked = (planet) => {
    return userLevel >= planet.unlockLevel
  }

  const handlePlanetClick = (planet) => {
    if (!isPlanetUnlocked(planet)) {
      addNotification({
        type: "warning",
        title: "Planet Locked",
        description: `Reach Level ${planet.unlockLevel} to unlock ${planet.name}`,
      })
      return
    }

    setSelectedPlanet(planet)
    setShowConfirmation(true)
  }

  const confirmSelection = () => {
    onPlanetSelect(selectedPlanet)
    setShowConfirmation(false)
  }

  const getCurrentStatus = (planet) => {
    if (planet.id === "mars" && realTimeData.mars?.hasDustStorm) {
      return {
        icon: "⚠️",
        text: "Current: Dust Storm",
        color: "#ff9800",
      }
    }
    return null
  }

  return (
    <div className="planet-selector">
      <div className="selector-header">
        <h2 className="section-title">
          <span className="title-icon">🌍</span>
          Planetary Selection
        </h2>
        <p className="section-subtitle">Choose your next colonization target</p>
      </div>

      <div className="planets-grid">
        {planets.map((planet) => {
          const isUnlocked = isPlanetUnlocked(planet)
          const currentStatus = getCurrentStatus(planet)

          return (
            <div
              key={planet.id}
              className={`planet-card ${!isUnlocked ? "locked" : ""}`}
              onClick={() => handlePlanetClick(planet)}
            >
              <div className="planet-visual">
                <div className="planet-sphere">
                  <div
                    className="planet-orb"
                    style={{
                      background: planet.sphere.gradient,
                      boxShadow: `0 0 30px ${planet.sphere.glow}`,
                    }}
                  ></div>
                  <div className="planet-glow" style={{ background: planet.sphere.glow }}></div>
                </div>
              </div>

              <div className="planet-info">
                <div className="planet-header">
                  <h3 className="planet-name">{planet.name}</h3>
                  <span className={`difficulty-badge ${planet.difficultyColor}`}>{planet.difficulty}</span>
                </div>

                <p className="planet-description">{planet.description}</p>

                {isUnlocked ? (
                  <>
                    <div className="environment-data">
                      <div className="env-item">
                        <span className="env-icon">🌡️</span>
                        <span className="env-value">
                          {realTimeData[planet.id]?.temperature?.toFixed(0) || planet.environment.temperature}°C
                        </span>
                      </div>
                      <div className="env-item">
                        <span className="env-icon">🌍</span>
                        <span className="env-value">{(planet.environment.gravity * 100).toFixed(1)}% of Earth</span>
                      </div>
                      <div className="env-item">
                        <span className="env-icon">💨</span>
                        <span className="env-value">{(planet.environment.atmosphere * 100).toFixed(0)}% of Earth</span>
                      </div>
                    </div>

                    <div className="threats-section">
                      <h4 className="threats-title">⚠️ Threats</h4>
                      <div className="threats-list">
                        {planet.threats.map((threat, index) => (
                          <span key={index} className="threat-tag">
                            {threat}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="resources-section">
                      <h4 className="resources-title">💎 Resources</h4>
                      <div className="resources-list">
                        {planet.resources.map((resource, index) => (
                          <span key={index} className="resource-tag">
                            {resource}
                          </span>
                        ))}
                      </div>
                    </div>

                    {currentStatus && (
                      <div className="current-status" style={{ color: currentStatus.color }}>
                        <span>{currentStatus.icon}</span>
                        <span>{currentStatus.text}</span>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="unlock-requirement">
                    <span className="lock-icon">🔒</span>
                    <span className="unlock-text">Unlock at Level {planet.unlockLevel}</span>
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="confirmation-overlay">
          <div className="confirmation-modal">
            <h3 className="confirmation-title">Confirm Planet Selection</h3>
            <p className="confirmation-text">
              You have selected <strong>{selectedPlanet?.name}</strong> for colonization.
            </p>
            <p className="confirmation-subtitle">
              This will begin your journey to establish a thriving colony on this world.
            </p>
            <div className="confirmation-buttons">
              <button className="cancel-button" onClick={() => setShowConfirmation(false)}>
                Cancel
              </button>
              <button className="confirm-button" onClick={confirmSelection}>
                Begin Colonization
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PlanetSelector


